import {
  Alert,
  Collapse,
  Descriptions,
  Modal,
  Spin,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import type { ModelMedicalDocumentResponse } from '@app/api/MedicalDocuments';

import { getDocumentStatusColor, getDocumentStatusLabel } from '../../../types';
import { useMedicalDocumentView } from '../hooks';

const { Text, Link } = Typography;

interface MedicalDocumentViewModalProps {
  documentId: number | null;
  onClose: () => void;
}

const formatDate = (date: string | null): string =>
  date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '—';

interface SignatureRow {
  role: string;
  name: string | null;
  date: string | null;
  thumbprint: string | null;
}

const collectSignatures = (
  document: ModelMedicalDocumentResponse
): SignatureRow[] => {
  const rows: SignatureRow[] = [];

  if (document.DoctorSignedByUserId) {
    rows.push({
      role: 'Врач',
      name: document.DoctorSignedByUserName,
      date: document.DoctorSignedDateTime,
      thumbprint: document.DoctorCertificateThumbprint,
    });
  }

  if (document.ChiefSignedByUserId) {
    rows.push({
      role: 'Руководитель',
      name: document.ChiefSignedByUserName,
      date: document.ChiefSignedDateTime,
      thumbprint: document.ChiefCertificateThumbprint,
    });
  }

  return rows;
};

export const MedicalDocumentViewModal = ({
  documentId,
  onClose,
}: MedicalDocumentViewModalProps) => {
  const { document, isLoading } = useMedicalDocumentView({ documentId });

  const signatures = document ? collectSignatures(document) : [];
  const errors = document?.Errors ?? [];

  // Промежуточное состояние «подписан врачом, ждёт организацию» выводим из
  // полей подписей: отдельного статуса в модели нет и не будет — бэк
  // подтвердил статусную модель DRAFT → PENDING_SIGN → SIGNED (us/10216).
  const isWaitingChiefSignature =
    !!document?.DoctorSignedByUserId && !document?.ChiefSignedByUserId;

  const semdXmlHref = document?.SemdXml
    ? `data:application/xml;charset=utf-8,${encodeURIComponent(
        document.SemdXml
      )}`
    : undefined;

  return (
    <Modal
      title="Медицинский документ"
      open={!!documentId}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      {isLoading || !document ? (
        <Spin />
      ) : (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Статус">
            <Tag color={getDocumentStatusColor(document.Status)}>
              {getDocumentStatusLabel(document.Status)}
            </Tag>
            {isWaitingChiefSignature && (
              <Text type="secondary"> подписан врачом, ждёт организацию</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Номер документа">
            {document.DocumentNumber || '—'}
          </Descriptions.Item>

          <Descriptions.Item label="Тип документа">
            {document.KindName || `Тип ${document.Kind}`}
          </Descriptions.Item>

          <Descriptions.Item label="Пациент">
            {document.PatientName || '—'}
          </Descriptions.Item>

          <Descriptions.Item label="Врач">
            {document.ResponsibleDoctorName || '—'}
          </Descriptions.Item>

          <Descriptions.Item label="Подписи">
            {signatures.length === 0
              ? '—'
              : signatures.map((signature, index) => (
                  <div key={index}>
                    <Text strong>{signature.role}</Text>
                    {' — '}
                    {signature.name || '—'}
                    {', '}
                    {formatDate(signature.date)}
                    {signature.thumbprint && (
                      <Text type="secondary">
                        {' '}
                        (отпечаток: {signature.thumbprint})
                      </Text>
                    )}
                  </div>
                ))}
          </Descriptions.Item>

          <Descriptions.Item label="EmdrId">
            {document.EmdrId || '—'}
          </Descriptions.Item>

          <Descriptions.Item label="Дата регистрации">
            {formatDate(document.RegistrationDateTime)}
          </Descriptions.Item>

          {document.RejectionReason && (
            <Descriptions.Item label="Причина отклонения">
              {document.RejectionReason}
            </Descriptions.Item>
          )}

          {errors.length > 0 && (
            <Descriptions.Item label="Ошибки Медфлекса">
              {errors.map((error, index) => (
                <Alert
                  key={index}
                  type="error"
                  showIcon
                  message={
                    error.code
                      ? `${error.code}: ${error.message}`
                      : error.message
                  }
                />
              ))}
            </Descriptions.Item>
          )}

          <Descriptions.Item label="СЭМД (XML)">
            {document.SemdXml ? (
              <>
                <Link
                  href={semdXmlHref}
                  download={`${document.DocumentNumber || 'semd'}.xml`}
                >
                  Скачать XML
                </Link>
                <Collapse
                  ghost
                  items={[
                    {
                      key: 'semd',
                      label: 'Показать XML',
                      children: (
                        <pre
                          style={{
                            maxHeight: 320,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                          }}
                        >
                          {document.SemdXml}
                        </pre>
                      ),
                    },
                  ]}
                />
              </>
            ) : (
              '—'
            )}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};
